import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { formatPhoneNumber } from '../../../../common/helpers/phoneHelper';
import { defaultLabels, statusOptions } from './utils';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ContactType } from '../../../../common/enums/ContactType';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IProspectStatus } from '../../../../common/models/prospect/IProspectStatus';
import { updateProspectStatusRoutine } from '../../routines';
import { IContact } from '../../../../common/models/contact/IContact';
import { formatDate } from '../../../../common/helpers/dateHelper';
import CustomSelect from '../../../../components/CustomSelect';
import { ProspectStatus } from '../../../../common/enums/ProspectStatus';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import { IProspectExtended } from '../../../../common/models/prospect/IProspectExtended';
import styles from './styles.module.scss';

interface IProspectItemProps {
  title: keyof typeof defaultLabels;
  value: string;
}

interface IProps {
  contact: IContact;
  role: ContactType;
  isUpdating: boolean;
  updateProspectStatus: IBindingCallback1<IProspectStatus>;
}

const ProspectItem: FunctionComponent<IProspectItemProps> = ({ title, value }) => (
  <div className="d-flex flex-column overflow-hidden">
    <span className={styles.title}>
      <FormattedMessage id={title} defaultMessage={defaultLabels[title]} />
    </span>
    {value && title === 'professionalProfile'
      ? (
        <FormattedMessage id="profileLink" defaultMessage="Link to profile">
          {placeholder => (
            // eslint-disable-next-line react/jsx-no-target-blank
            <a className="link" target="_blank" href={value}>{placeholder as string}</a>
          )}
        </FormattedMessage>
      )
      : <span className={`${styles.value} ${value ? '' : 'secondary-text'}`}>{value || 'n/a'}</span>}
  </div>
);

const ProspectPane: FunctionComponent<IProps> = ({
  contact,
  isUpdating,
  updateProspectStatus
}) => {
  const prospect: IProspectExtended = contact.prospect || {};
  const { firstName, lastName, email, phoneNumber } = contact;
  const onChangeStatus = (updatedStatus: ProspectStatus) => {
    if (updatedStatus !== prospect.status) {
      updateProspectStatus({ ids: [prospect.id], status: updatedStatus });
    }
  };

  return (
    <div className={styles.prospectProfile}>
      <LoaderWrapper transparent={isUpdating} loading={isUpdating || !prospect?.id}>
        <div className="d-flex justify-content-between align-items-center">
          <span className="t-18">
            <FormattedMessage id="contactProfile.prospect.title" defaultMessage="Prospect information" />
          </span>
          <div className="d-flex align-items-center">
            <span className={styles.dropdownLabel}>
              <FormattedMessage id="status" defaultMessage="Status" />
            </span>
            <CustomSelect
              value={prospect?.status}
              options={statusOptions}
              onChange={value => onChangeStatus(value as ProspectStatus)}
            />
          </div>
        </div>
        <div className={styles.container}>
          <div className={styles.prospectInfo}>
            <ProspectItem title="fullName" value={`${firstName} ${lastName}`} />
            <ProspectItem title="professionalProfile" value={prospect.profileLink} />
            <ProspectItem title="email" value={email} />
            <ProspectItem title="phoneNumber" value={phoneNumber && formatPhoneNumber(phoneNumber)} />
            <ProspectItem title="currentRole" value={prospect.currentPosition} />
            <ProspectItem title="currentEmployer" value={prospect.currentCompany} />
          </div>
          <div className={`${styles.containerFooter} ${styles.prospectInfo}`}>
            <ProspectItem title="sourceCandidate" value={prospect.sourceCandidate} />
            <ProspectItem title="optInDate" value={formatDate(prospect.optInDate)} />
          </div>
        </div>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  contactProfile: {
    contact: {
      contact,
      role,
      isUpdating
    }
  }
}: IAppState) => ({ contact, role, isUpdating });

const mapDispachToProps = {
  updateProspectStatus: updateProspectStatusRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ProspectPane);
